import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Award, MapPin, Clock, Quote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/success-stories`);
      setStories(response.data);
    } catch (error) {
      // Fallback data
      setStories([
        {
          id: '1',
          name: 'David Rodriguez',
          title: 'Regional Vice President',
          location: 'Miami, FL',
          story: 'I started with ShopCompareInsure as a part-time advisor while working my corporate job. Within 3 years, I was earning more than my previous salary and had built a team of 25 agents.',
          achievement: 'Built a team of 50+ agents generating $2M+ in annual premium',
          years_with_company: 5
        },
        {
          id: '2',
          name: 'Jennifer Kim',
          title: 'Senior Marketing Director',
          location: 'Seattle, WA',
          story: 'Coming from a teaching background, I never imagined I could build a successful financial services career. The training program gave me everything I needed to succeed.',
          achievement: 'Helped over 500 families secure financial protection',
          years_with_company: 4
        },
        {
          id: '3',
          name: 'Marcus Thompson',
          title: 'Executive Vice President',
          location: 'Atlanta, GA',
          story: 'What attracted me to ShopCompareInsure was the ability to truly help families while building generational wealth for my own family. The business model is designed for long-term success.',
          achievement: 'Organization of 200+ agents across 12 states',
          years_with_company: 7
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="success-stories-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Agent Success Stories
            </h1>
            <p className="text-slate-300 text-xl">
              Real stories from advisors who have built successful careers with ShopCompareInsure.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-slate-800 h-96 rounded-lg"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {stories.map((story) => (
                <div
                  key={story.id}
                  className="bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-colors"
                >
                  <Quote className="w-10 h-10 text-[#C5A059]/30 mb-4" />
                  
                  <p className="text-slate-300 text-lg leading-relaxed mb-6 italic">
                    "{story.story}"
                  </p>

                  <div className="bg-[#C5A059]/10 px-4 py-3 mb-6 border-l-2 border-[#C5A059]">
                    <p className="text-[#C5A059] text-sm font-semibold">{story.achievement}</p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">{story.name}</p>
                      <p className="text-[#C5A059] text-sm">{story.title}</p>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <div className="flex items-center justify-end space-x-1 mb-1">
                        <MapPin className="w-3 h-3" />
                        <span>{story.location}</span>
                      </div>
                      <div className="flex items-center justify-end space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{story.years_with_company} years</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Write Your Own Success Story</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join the growing number of advisors building rewarding careers with ShopCompareInsure.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Start Your Journey
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;
